import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, Tabs, TextField } from '../components/ui';
import { Wordmark } from '../components/brand/Wordmark.jsx';
import { TenantBackdrop } from '../components/brand/TenantBackdrop.jsx';
import { resolveTenantSlug, tenantIdentity, tenantLabel } from '../utils/tenantIdentity.js';
import { useAuth } from '../context/AuthContext';

/**
 * Classifies a thrown auth error into copy a non-technical user can act on
 * (spec §10.1: "verständliche Fehlermeldungen ohne technische Rohdaten").
 *
 * The raw message is deliberately never rendered — `/api/auth/login` answers
 * with one generic string for every bad-credential case precisely so the form
 * cannot be used to enumerate accounts, and echoing transport errors like
 * "Failed to fetch" tells the user nothing they can fix.
 */
function classifyAuthError(error) {
  const message = String(error?.message ?? '');
  if (/failed to fetch|networkerror|load failed/i.test(message) || navigator.onLine === false) {
    return 'offline';
  }
  if (/exist|unique|409|taken/i.test(message)) return 'userExists';
  return 'credentials';
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [tab, setTab] = useState('signin');
  const [oidc, setOidc] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  // The session-expired state is explicit rather than inferred: whoever bounces
  // the user here (route guard, 401 interceptor) sets `?session=expired`, so a
  // fresh visit to /login never shows a scary banner.
  const sessionExpired =
    new URLSearchParams(location.search).get('session') === 'expired' ||
    Boolean(location.state?.sessionExpired);

  const tenantSlug = useMemo(
    () => resolveTenantSlug(location.search, typeof window === 'undefined' ? null : window.localStorage),
    [location.search]
  );

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/oidc/status')
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setOidc(data?.enabled ? data : null);
      })
      .catch(() => {
        if (!cancelled) setOidc(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [signIn, setSignIn] = useState({ identifier: '', password: '' });
  const [signInError, setSignInError] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);

  const [signUp, setSignUp] = useState({ username: '', email: '', password: '', confirm: '' });
  const [signUpError, setSignUpError] = useState('');
  const [signUpFieldErrors, setSignUpFieldErrors] = useState({});
  const [signUpLoading, setSignUpLoading] = useState(false);

  async function handleSignIn(event) {
    event.preventDefault();
    setSignInError('');
    setSignInLoading(true);
    try {
      await login(signIn.identifier.trim(), signIn.password);
      navigate('/');
    } catch (error) {
      setSignInError(t(`auth.error.${classifyAuthError(error)}`));
    } finally {
      setSignInLoading(false);
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    setSignUpError('');

    // Field-level first: a recoverable input error belongs next to its field,
    // not in a banner the user has to map back onto the form (spec §12).
    const fieldErrors = {};
    if (signUp.password.length < 8) fieldErrors.password = t('auth.error.passwordTooShort');
    if (signUp.password !== signUp.confirm) fieldErrors.confirm = t('auth.error.passwordMismatch');
    setSignUpFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setSignUpLoading(true);
    try {
      await register(signUp.username.trim(), signUp.email.trim() || undefined, signUp.password);
      navigate('/');
    } catch (error) {
      setSignUpError(t(`auth.error.${classifyAuthError(error)}`));
    } finally {
      setSignUpLoading(false);
    }
  }

  /**
   * The whole screen on a hosted instance: one way in, and it is named.
   *
   * The label says nyxCore rather than a bare "Anmelden" because the button
   * leaves the site — a person who lands on a different domain's login should
   * have been told that a moment earlier, not be left wondering whether they
   * were redirected somewhere they did not intend.
   */
  const oidcPanel = (
    <div className="etch-auth-screen__form">
      <h1 className="etch-auth-screen__title">{t('auth.signInTitle')}</h1>
      <p className="etch-auth-screen__desc">{t('auth.oidcOnlyDesc')}</p>

      {/* A real navigation, not fetch(): the OIDC flow leaves the SPA. */}
      <a
        className="etch-button"
        data-variant="primary"
        data-block="true"
        href={oidc?.loginUrl || '/api/auth/oidc/login'}
      >
        {t('auth.oidcButton')}
      </a>

      <p className="etch-auth-screen__hint">{t('auth.oidcRedirectHint')}</p>
    </div>
  );

  const signInPanel = (
    <>
      <form className="etch-auth-screen__form" onSubmit={handleSignIn} noValidate>
        <h1 className="etch-auth-screen__title">{t('auth.signInTitle')}</h1>

        <TextField
          label={t('auth.identifier')}
          hint={t('auth.identifierHint')}
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck="false"
          required
          value={signIn.identifier}
          onChange={(e) => setSignIn((f) => ({ ...f, identifier: e.target.value }))}
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          value={signIn.password}
          onChange={(e) => setSignIn((f) => ({ ...f, password: e.target.value }))}
        />

        {signInError && (
          <Alert tone="danger" title={t('auth.error.signInTitle')}>
            {signInError}
          </Alert>
        )}

        <Button type="submit" block loading={signInLoading} disabled={signInLoading}>
          {t('auth.login')}
        </Button>

        <div className="etch-auth-screen__footer">
          <Button variant="ghost" size="compact" onClick={() => setResetOpen(true)}>
            {t('auth.passwordReset')}
          </Button>
        </div>
      </form>
    </>
  );

  const registerPanel = (
    <form className="etch-auth-screen__form" onSubmit={handleRegister} noValidate>
      <h1 className="etch-auth-screen__title">{t('auth.createAccount')}</h1>

      <TextField
        label={t('auth.username')}
        type="text"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck="false"
        required
        value={signUp.username}
        onChange={(e) => setSignUp((f) => ({ ...f, username: e.target.value }))}
      />
      <TextField
        label={t('auth.emailOptional')}
        type="email"
        autoComplete="email"
        value={signUp.email}
        onChange={(e) => setSignUp((f) => ({ ...f, email: e.target.value }))}
      />
      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="new-password"
        required
        hint={t('auth.passwordHint')}
        error={signUpFieldErrors.password}
        value={signUp.password}
        onChange={(e) => setSignUp((f) => ({ ...f, password: e.target.value }))}
      />
      <TextField
        label={t('auth.passwordConfirm')}
        type="password"
        autoComplete="new-password"
        required
        error={signUpFieldErrors.confirm}
        value={signUp.confirm}
        onChange={(e) => setSignUp((f) => ({ ...f, confirm: e.target.value }))}
      />

      {signUpError && (
        <Alert tone="danger" title={t('auth.error.registerTitle')}>
          {signUpError}
        </Alert>
      )}

      <Button type="submit" block loading={signUpLoading} disabled={signUpLoading}>
        {t('auth.createAccount')}
      </Button>
    </form>
  );

  const identity = useMemo(() => tenantIdentity(tenantSlug), [tenantSlug]);
  const label = tenantLabel(tenantSlug);

  return (
    <div className="etch-auth-screen">
      <TenantBackdrop slug={tenantSlug} />

      <div className="etch-auth-screen__card">
        <header className="etch-auth-screen__brand">
          <Wordmark size="lg" />
          <p className="etch-auth-screen__tagline">{t('brandKit.tagline')}</p>
        </header>

        {label && (
          <span
            className="etch-auth-screen__tenant"
            data-accent={identity?.accent}
            aria-label={t('auth.tenantContext', { tenant: label })}
          >
            {label}
          </span>
        )}

        {sessionExpired && (
          <Alert tone="warning" title={t('states.sessionExpired')}>
            {t('auth.sessionExpired')}
          </Alert>
        )}

        {/* A configured OIDC client makes nyx-platform the only identity
            authority here, so the local form and self-registration are not
            hidden but absent — a second way in that the server refuses anyway
            is an invitation to a dead end. Standalone installs, which have no
            OIDC, keep the form untouched. */}
        {oidc?.enabled ? (
          oidcPanel
        ) : (
          <Tabs
            ariaLabel={t('auth.login')}
            value={tab}
            onChange={setTab}
            items={[
              { id: 'signin', label: t('auth.login'), panel: signInPanel },
              { id: 'register', label: t('auth.createAccount'), panel: registerPanel }
            ]}
          />
        )}
      </div>

      {!oidc?.enabled && (
        <Dialog
          open={resetOpen}
          onClose={() => setResetOpen(false)}
          title={t('auth.passwordReset')}
          closeLabel={t('a11y.closeDialog')}
          footer={
            <Button variant="secondary" onClick={() => setResetOpen(false)}>
              {t('auth.close')}
            </Button>
          }
        >
          <p>{t('auth.passwordResetLocal')}</p>
        </Dialog>
      )}
    </div>
  );
}
